/**
 * Live hand / finger guide overlay during Practice (pure Canvas).
 */

import { defaultCPositionFinger } from "@piano-tutor/shared";

export class HandsGuideOverlay {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private midis: number[] = [];
  private fingers: Map<number, number> = new Map();
  private visible = false;
  private raf = 0;
  private t0 = performance.now();
  private low = 36;
  private high = 96;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D unavailable");
    this.ctx = ctx;
    this.resize();
    window.addEventListener("resize", () => this.resize());
  }

  resize(): void {
    const parent = this.canvas.parentElement;
    if (!parent) return;
    const cssW = parent.clientWidth || 800;
    const cssH = parent.clientHeight || 220;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.floor(cssW * this.dpr);
    this.canvas.height = Math.floor(cssH * this.dpr);
    this.canvas.style.width = `${cssW}px`;
    this.canvas.style.height = `${cssH}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    if (this.visible) this.draw((performance.now() - this.t0) / 1000);
  }

  setExpected(midis: number[], fingers?: Record<number, number> | null): void {
    this.midis = [...midis];
    this.fingers = new Map();
    if (fingers) {
      for (const [k, v] of Object.entries(fingers)) {
        this.fingers.set(Number(k), v);
      }
    }
    if (this.midis.length && !this.visible) this.show();
    else if (!this.midis.length) this.hide();
    else this.draw((performance.now() - this.t0) / 1000);
  }

  show(): void {
    this.visible = true;
    this.canvas.style.opacity = "1";
    this.start();
  }

  hide(): void {
    this.visible = false;
    this.midis = [];
    this.fingers.clear();
    this.canvas.style.opacity = "0";
    cancelAnimationFrame(this.raf);
  }

  setRange(low: number, high: number): void {
    this.low = low;
    this.high = high;
    if (this.visible) this.draw((performance.now() - this.t0) / 1000);
  }

  private start(): void {
    cancelAnimationFrame(this.raf);
    const loop = () => {
      if (!this.visible) return;
      this.draw((performance.now() - this.t0) / 1000);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  private whiteKeys(): number[] {
    const whites: number[] = [];
    for (let m = this.low; m <= this.high; m++) {
      const pc = m % 12;
      if (pc === 1 || pc === 3 || pc === 6 || pc === 8 || pc === 10) continue;
      whites.push(m);
    }
    return whites;
  }

  private draw(t: number): void {
    const w = this.canvas.width / this.dpr;
    const h = this.canvas.height / this.dpr;
    const ctx = this.ctx;
    ctx.clearRect(0, 0, w, h);
    if (!this.midis.length) return;

    const whites = this.whiteKeys();
    const ww = w / Math.max(1, whites.length);
    const pulse = 0.5 + 0.5 * Math.sin(t * 4);

    // Soft wrist / palm silhouette
    ctx.fillStyle = `rgba(232,184,150,${0.2 + pulse * 0.15})`;
    ctx.beginPath();
    ctx.ellipse(w * 0.5, h * 0.92, w * 0.22, h * 0.18, 0, 0, Math.PI * 2);
    ctx.fill();

    for (const midi of this.midis) {
      const finger = this.fingers.get(midi) ?? defaultCPositionFinger(midi) ?? 1;
      let wi = whites.findIndex((x) => x === midi);
      if (wi < 0) {
        // black key — nestle near left white
        let nearest = 0;
        for (let i = 0; i < whites.length; i++) {
          if (whites[i] < midi) nearest = i;
        }
        const x = nearest * ww + ww * 0.72;
        this.drawFinger(ctx, x, h * 0.35, finger, true, pulse);
      } else {
        const x = wi * ww + ww * 0.5;
        this.drawFinger(ctx, x, h * 0.55, finger, false, pulse);
      }
    }
  }

  private drawFinger(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    finger: number,
    black: boolean,
    pulse: number,
  ): void {
    const len = black ? 36 : 52;
    ctx.save();
    ctx.translate(x, y);
    ctx.fillStyle = "#e8b896";
    ctx.strokeStyle = "rgba(80,40,20,0.45)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    const fw = 14;
    const fh = len + 10;
    ctx.moveTo(-fw / 2 + 4, -len);
    ctx.lineTo(fw / 2 - 4, -len);
    ctx.quadraticCurveTo(fw / 2, -len, fw / 2, -len + 4);
    ctx.lineTo(fw / 2, fh - len - 4);
    ctx.quadraticCurveTo(fw / 2, fh - len, fw / 2 - 4, fh - len);
    ctx.lineTo(-fw / 2 + 4, fh - len);
    ctx.quadraticCurveTo(-fw / 2, fh - len, -fw / 2, fh - len - 4);
    ctx.lineTo(-fw / 2, -len + 4);
    ctx.quadraticCurveTo(-fw / 2, -len, -fw / 2 + 4, -len);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    // knuckle highlight
    ctx.fillStyle = "rgba(255,240,230,0.35)";
    ctx.beginPath();
    ctx.ellipse(0, -len * 0.35, 5, 4, 0, 0, Math.PI * 2);
    ctx.fill();
    // fingertip glow
    ctx.fillStyle = `rgba(125,255,208,${0.35 * pulse})`;
    ctx.beginPath();
    ctx.arc(0, -len + 4, 9, 0, Math.PI * 2);
    ctx.fill();
    // finger number
    ctx.fillStyle = "#1a2218";
    ctx.font = "bold 12px Georgia, serif";
    ctx.textAlign = "center";
    ctx.fillText(String(finger), 0, -len * 0.55);
    ctx.restore();
  }
}
